import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { cva, type VariantProps } from 'class-variance-authority';
import { ReactComponent as MenuWideIcon } from '../../../../icons/svg/menu-wide.svg';
import { ReactComponent as LogoIcon } from '../../../../icons/svg/logo-new-black.svg';
import { ReactComponent as LikeIcon } from '../../../../icons/svg/like.svg';
import { ReactComponent as UserIcon } from '../../../../icons/svg/user.svg';
import { ReactComponent as InboxIcon } from '../../../../icons/svg/message.svg';
import { ReactComponent as OrdersIcon } from '../../../../icons/svg/cart.svg';
import { ReactComponent as NotificationsIcon } from '../../../../icons/svg/bell.svg';
import { ReactComponent as LogoutIcon } from '../../../../icons/svg/logout.svg';
import { ReactComponent as FolderIcon } from '../../../../icons/svg/folder.svg';
import { ReactComponent as HouseIcon } from '../../../../icons/svg/house.svg';
import { Badge } from '../../../../components/badge';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../../../../components/popover';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from '../../../../components/avatar';
import { UserMenuElement } from './UserMenuElement';
import type { HeaderNavLinks, HeaderUserData } from '../Header';

export const _desktopUserMenuTriggerVariants = cva(
    'flex cursor-pointer items-center gap-x-2.5',
    {
        variants: {
            color: {
                light: 'text-white',
                dark: 'text-gray-900',
            },
        },
        defaultVariants: {
            color: 'light',
        },
    },
);
// Use px, not rem: this component is shared with Business Manager, whose root
// rem is ~1px (`get-vw(1)`) and whose Tailwind tokens are pre-multiplied ×16.
// Arbitrary rem values bypass that multiplier, so `0.6875rem`/`2.5625rem` would
// collapse to <1px in BM, making the menu icon vanish. px (= the icon's native
// 41×11 viewBox) renders identically in both Remix and BM (BC-4489).
const _desktopUserMenuIconVariants = cva('h-[11px] w-[41px]', {
    variants: {
        color: {
            light: 'text-white',
            dark: 'text-gray-900',
        },
    },
    defaultVariants: {
        color: 'dark',
    },
});

interface DesktopUserMenuProps extends VariantProps<
    typeof _desktopUserMenuTriggerVariants
> {
    userData: HeaderUserData;
    onLogout: () => void;
    additionalUserMenuItems?: HeaderNavLinks;
    allowMemberGuides?: boolean;
    allowMemberOrders?: boolean;
}

export const DesktopUserMenu = (props: DesktopUserMenuProps) => {
    const {
        color,
        userData,
        onLogout,
        additionalUserMenuItems,
        allowMemberGuides = false,
        allowMemberOrders = false,
    } = props;
    const {
        avatarSrc = '',
        firstName,
        lastName = '',
        notificationsCount,
        professionalSections,
        showCountryLabels,
        inboxMenu,
    } = userData;
    const shouldShowMyProjects = userData.isBuildHubEnabled;
    const [open, setOpen] = useState(false);
    const location = useLocation();

    // Close the menu on navigation.
    useEffect(() => {
        setOpen(false);
    }, [location]);

    return (
        <div className="relative hidden items-center md:flex">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger>
                    <div className={_desktopUserMenuTriggerVariants({ color })}>
                        <MenuWideIcon
                            className={_desktopUserMenuIconVariants({ color })}
                        />
                        <div className="relative">
                            <Avatar size="square48">
                                <AvatarImage src={avatarSrc} />
                                <AvatarFallback>
                                    {firstName[0]}
                                    {lastName[0]}
                                </AvatarFallback>
                            </Avatar>
                            {notificationsCount > 0 ? (
                                <div className="absolute -top-1 -right-1 flex">
                                    <Badge
                                        size="centeredCompact"
                                        text={
                                            notificationsCount > 9
                                                ? 'small'
                                                : 'default'
                                        }
                                        color={'default'}
                                    >
                                        {notificationsCount > 9
                                            ? '9+'
                                            : notificationsCount}
                                    </Badge>
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>
                </PopoverTrigger>
                <PopoverContent
                    paddings="small"
                    align="end"
                    className="max-w-64"
                    forceMount
                >
                    <div className="flex flex-col">
                        <div className="flex h-8 items-center px-2">
                            <span className="text-sm font-medium">
                                {firstName} {lastName}
                            </span>
                        </div>
                        <UserMenuElement
                            icon={
                                <UserIcon className="mx-auto size-4.5 stroke-gray-900" />
                            }
                            title="My Account"
                            to="/app/settings/profile"
                        />
                        <UserMenuElement
                            icon={<InboxIcon />}
                            title={inboxMenu.title}
                            to={inboxMenu.to}
                            external={inboxMenu.isNonRemixRoute}
                            badgeCount={userData.inboxCount}
                        />
                        {shouldShowMyProjects ? (
                            <UserMenuElement
                                icon={<HouseIcon />}
                                title="My Projects"
                                to="/app/my-projects"
                            />
                        ) : null}
                        <UserMenuElement
                            icon={<LikeIcon />}
                            title="My Design Boards"
                            to="/app/boards"
                        />
                        {allowMemberGuides && (
                            <UserMenuElement
                                icon={<FolderIcon />}
                                title="Guides"
                                to="/app/guides"
                            />
                        )}
                        {allowMemberOrders && (
                            <UserMenuElement
                                icon={<OrdersIcon />}
                                title="Orders"
                                to="/member/orders"
                                external={true}
                                badgeCount={userData.orderNotificationCount}
                            />
                        )}
                        <UserMenuElement
                            icon={<NotificationsIcon />}
                            title="Notifications"
                            to="/app/notifications"
                            badgeCount={userData.websiteNotificationCount}
                        />
                        {professionalSections.length > 0 ? (
                            <>
                                <div className="my-2.5 h-px border-t-0 bg-gray-400"></div>
                                {professionalSections.map((section) => (
                                    <div key={section.country}>
                                        {showCountryLabels && (
                                            <div className="px-2 pt-2 pb-2">
                                                <span className="text-sm font-medium">
                                                    {section.label}
                                                </span>
                                            </div>
                                        )}
                                        {section.professionals.map(
                                            (professional) => {
                                                const logoSrc =
                                                    professional.companyLogo48Resized ||
                                                    professional.logo;
                                                return (
                                                    <UserMenuElement
                                                        key={professional.id}
                                                        icon={
                                                            <Avatar
                                                                size="square20"
                                                                className="p-0.5"
                                                                style={{
                                                                    backgroundColor:
                                                                        professional.logoBackgroundColor ??
                                                                        (logoSrc
                                                                            ? 'white'
                                                                            : '#C7C7C7'),
                                                                }}
                                                            >
                                                                <AvatarImage
                                                                    src={
                                                                        logoSrc
                                                                    }
                                                                    fit="contain"
                                                                />
                                                                <AvatarFallback className="bg-transparent" />
                                                            </Avatar>
                                                        }
                                                        title={
                                                            professional.title
                                                        }
                                                        to={`/business/${professional.urlSegment}`}
                                                        external={true}
                                                    />
                                                );
                                            },
                                        )}
                                    </div>
                                ))}
                            </>
                        ) : null}
                        <div className="my-2.5 h-px border-t-0 bg-gray-400"></div>
                        <UserMenuElement
                            icon={<LogoIcon />}
                            title="List a Business"
                            to="/advertising"
                            external={true}
                        />
                        {additionalUserMenuItems &&
                        additionalUserMenuItems.links &&
                        additionalUserMenuItems.links.length > 0 ? (
                            <>
                                <div className="my-2.5 h-px border-t-0 bg-gray-400"></div>
                                {additionalUserMenuItems.links.map((item) => (
                                    <UserMenuElement
                                        key={item.title}
                                        icon={<LogoIcon />}
                                        title={item.title}
                                        {...(item.onClick
                                            ? { onClick: item.onClick }
                                            : { to: item.to })}
                                        external={item.external}
                                    />
                                ))}
                            </>
                        ) : null}
                        <div className="my-2.5 h-px border-t-0 bg-gray-400"></div>
                        <UserMenuElement
                            icon={<LogoutIcon />}
                            title="Logout"
                            onClick={onLogout}
                        />
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};
